import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Box,
  Card,
  Chip,
  Grid,
  Stack,
  OutlinedInput,
  InputAdornment,
  Divider,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  CalendarToday as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useGarage } from '../../contexts/GarageContext';
import { apiService } from '../../services/api';
import CustomerContactEditor from './CustomerContactEditor';
import AddServiceModal from './AddServiceModal';
import AddCustomerModal from './AddCustomerModal';
import { getLocalDateString } from '../../utils/dateUtils';
import { getCategoryStyle } from '../../utils/categoryConstants';

const NewAppointmentModal = ({
  open,
  onClose,
  garageId,
  onSuccess,
  onError,
  availableProducts = [],
  availableAddons = [],
  rescheduleMode = false,
  rescheduleBookingId = null,
  prefilledDate = null,
  prefilledTime = null,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedGarage } = useGarage();

  // Form states
  const [selectedVehicleCustomer, setSelectedVehicleCustomer] = useState(null);
  const [vehicleCustomerSearch, setVehicleCustomerSearch] = useState('');
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    if (prefilledDate) {
      return prefilledDate instanceof Date ? prefilledDate : new Date(prefilledDate);
    }
    return new Date();
  });
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(prefilledTime || null);
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [showServicesPicker, setShowServicesPicker] = useState(false);
  const [showVehicleCustomerPicker, setShowVehicleCustomerPicker] = useState(false);

  // Modal states
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [addServiceModalOpen, setAddServiceModalOpen] = useState(false);
  const [addCustomerModalOpen, setAddCustomerModalOpen] = useState(false);

  // Refs for pickers
  const dateTimePickerRef = useRef(null);
  const servicesPickerRef = useRef(null);
  const vehicleCustomerPickerRef = useRef(null);

  // Refs for scrolling inputs to top
  const servicesInputRef = useRef(null);
  const dateTimeInputRef = useRef(null);
  const customerInputRef = useRef(null);
  // Refs for auto-focus search inputs
  const customerSearchInputRef = useRef(null);
  const servicesSearchInputRef = useRef(null);

  // Data states
  const [garageCustomers, setGarageCustomers] = useState([]);
  const [filteredGarageCustomers, setFilteredGarageCustomers] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rescheduleBookingData, setRescheduleBookingData] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searching, setSearching] = useState(false);
  const [customTimeInput, setCustomTimeInput] = useState('');
  const [showCustomTimeInput, setShowCustomTimeInput] = useState(false);
  const [customTimeError, setCustomTimeError] = useState('');

  // Customer name/mobile inline edit open
  const [editingContact, setEditingContact] = useState(false);

  // Manual MOT refresh status per customer id: 'loading' | 'error'
  const [motRefreshStatus, setMotRefreshStatus] = useState({});

  // Custom ("Others") service states
  const [customServiceName, setCustomServiceName] = useState('');
  const [customServicePrice, setCustomServicePrice] = useState('');

  // Helper function to scroll input to top of modal
  const scrollToTop = (elementRef) => {
    if (elementRef.current) {
      elementRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  };

  // Reset form state for reschedule mode
  const resetFormState = () => {
    setSelectedVehicleCustomer(null);
    setSelectedServices([]);
    setEditingContact(false);
    setCustomServiceName('');
    setCustomServicePrice('');
    setMotRefreshStatus({});
    setSelectedDate(() => {
      if (prefilledDate) {
        return prefilledDate instanceof Date ? prefilledDate : new Date(prefilledDate);
      }
      return new Date();
    });
    if (prefilledTime) {
      // Format time for display
      const formatTimeDisplay = (time) => {
        const [hours, minutes] = time.split(':');
        const hour12 = parseInt(hours) > 12 ? parseInt(hours) - 12 : parseInt(hours) === 0 ? 12 : parseInt(hours);
        const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
        return `${hour12}:${minutes} ${ampm}`;
      };
      
      setSelectedTimeSlot({
        time: prefilledTime,
        display: formatTimeDisplay(prefilledTime)
      });
    } else {
      setSelectedTimeSlot(null);
    }
    setShowVehicleCustomerPicker(false);
    setShowDateTimePicker(false);
    setShowServicesPicker(false);
    setRescheduleBookingData(null);
  };

  // Fetch booking data for reschedule mode
  useEffect(() => {
    const fetchBookingData = async () => {
      if (rescheduleMode && rescheduleBookingId && open) {
        try {
          resetFormState();
          setLoading(true);
          const response = await apiService.getBooking(rescheduleBookingId);
          const bookingData = response.data || response;
          setRescheduleBookingData(bookingData);
        } catch (error) {
          console.error('Error fetching booking data:', error);
          if (onError) {
            onError('Failed to load booking details');
          }
        } finally {
          setLoading(false);
        }
      }
    };

    fetchBookingData();
  }, [rescheduleMode, rescheduleBookingId, open, onError]);

  // Fetch initial data when modal opens
  useEffect(() => {
    if (open && garageId) {
      fetchInitialData();
      if (!rescheduleMode) {
        resetFormState();
      }
    }
  }, [open, garageId, rescheduleMode]);

  // Handle prefilled date and time for new appointments
  useEffect(() => {
    if (open && !rescheduleMode) {
      if (prefilledDate) {
        const date = prefilledDate instanceof Date ? prefilledDate : new Date(prefilledDate);
        setSelectedDate(date);
      }
      if (prefilledTime) {
        // Format time for display (e.g., "09:00" -> "9:00 AM")
        const formatTimeDisplay = (time) => {
          const [hours, minutes] = time.split(':');
          const hour12 = parseInt(hours) > 12 ? parseInt(hours) - 12 : parseInt(hours) === 0 ? 12 : parseInt(hours);
          const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
          return `${hour12}:${minutes} ${ampm}`;
        };
        
        setSelectedTimeSlot({
          time: prefilledTime,
          display: formatTimeDisplay(prefilledTime)
        });
      }
    }
  }, [open, rescheduleMode]);

  // Pre-populate reschedule data
  useEffect(() => {
    if (rescheduleMode && rescheduleBookingData && availableProducts.length > 0) {
      if (rescheduleBookingData.customer) {
        setSelectedVehicleCustomer(rescheduleBookingData.customer);
      }

      const originalBookingDate = new Date(rescheduleBookingData.booking.booking_date);
      setSelectedDate(originalBookingDate);

      const originalTime = rescheduleBookingData.booking.booking_time;
      const displayTime = formatTimeDisplay(originalTime);

      setSelectedTimeSlot({
        time: originalTime,
        duration_minutes: rescheduleBookingData.booking.duration_minutes || 60,
        display: displayTime,
        is_original: true,
      });

      const preSelectedServices = [];

      if (rescheduleBookingData.products) {
        rescheduleBookingData.products.forEach((product) => {
          const matchingProduct = availableProducts.find((p) => p.id === product.id);
          if (matchingProduct) {
            preSelectedServices.push({
              id: product.id,
              name: product.name,
              type: 'product',
              garage_price: product.garage_price || product.default_price,
              duration_minutes: product.duration_minutes || 60,
            });
          }
        });
      }

      if (rescheduleBookingData.addons) {
        rescheduleBookingData.addons.forEach((addon) => {
          const matchingAddon = availableAddons.find((a) => a.id === addon.id);
          if (matchingAddon) {
            preSelectedServices.push({
              id: addon.id,
              name: addon.name,
              type: 'addon',
              garage_price: addon.garage_price || addon.default_price,
              duration_minutes: addon.duration_minutes || 30,
            });
          }
        });
      }

      setSelectedServices(preSelectedServices);
      setShowDateTimePicker(true);

      setTimeout(() => {
        scrollToTop(dateTimeInputRef);
      }, 500);
    }
  }, [rescheduleMode, rescheduleBookingData]);

  // Memoize selected services for stable dependency
  const selectedServicesIds = React.useMemo(
    () => selectedServices.map((s) => s.id).sort().join(','),
    [selectedServices]
  );

  // Handle click outside to close pickers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateTimePickerRef.current && !dateTimePickerRef.current.contains(event.target)) {
        setShowDateTimePicker(false);
      }
      if (servicesPickerRef.current && !servicesPickerRef.current.contains(event.target)) {
        setShowServicesPicker(false);
      }
      if (
        vehicleCustomerPickerRef.current &&
        !vehicleCustomerPickerRef.current.contains(event.target)
      ) {
        setShowVehicleCustomerPicker(false);
      }
    };

    if (showDateTimePicker || showServicesPicker || showVehicleCustomerPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDateTimePicker, showServicesPicker, showVehicleCustomerPicker]);

  // Search garage customers
  const searchGarageCustomers = async () => {
    if (!vehicleCustomerSearch.trim()) return;

    setSearching(true);
    setSearchPerformed(false);

    try {
      const response = await apiService.getGarageCustomers(garageId, {
        search: vehicleCustomerSearch.trim(),
      });
      const customers = response.data || [];
      setGarageCustomers(customers);
      setFilteredGarageCustomers(customers);
      setSearchPerformed(true);
    } catch (error) {
      console.error('Error searching garage customers:', error);
      setGarageCustomers([]);
      setFilteredGarageCustomers([]);
      setSearchPerformed(true);
    } finally {
      setSearching(false);
    }
  };

  const fetchInitialData = async () => {
    try {
      setGarageCustomers([]);
      setFilteredGarageCustomers([]);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchAvailableSlots = React.useCallback(async () => {
    if (!selectedDate || !garageId) {
      setAvailableSlots([]);
      return;
    }

    setLoading(true);
    try {
      const dateStr = getLocalDateString(selectedDate);

      const productIds = selectedServices
        .filter((s) => availableProducts.some((p) => p.id === s.id))
        .map((s) => s.id);
      const addonIds = selectedServices
        .filter((s) => availableAddons.some((a) => a.id === s.id))
        .map((s) => s.id);

      try {
        const response = await apiService.getGarageAvailability(garageId, dateStr, {
          product_ids: productIds,
          addon_ids: addonIds,
        });

        const availabilityData = response.data;
        const timeSlots = [];

        if (availabilityData?.slots && availabilityData.slots.length > 0) {
          const daySlots = availabilityData.slots[0]?.time_slots || [];
          daySlots.forEach((slot) => {
            timeSlots.push({
              time: slot.time,
              display: formatTimeDisplay(slot.time),
              available: slot.is_available,
              slot_id: slot.slot_id,
              duration_minutes: slot.duration_minutes,
              price: slot.price,
              booking_count: slot.booking_count || 0,
              availability_status: slot.availability_status || 'available',
            });
          });
        }

        setAvailableSlots(timeSlots);
      } catch (apiError) {
        console.error('Error fetching garage availability:', apiError);
        console.error('Response data:', apiError.response?.data);
        console.error('Request details:', { garageId, dateStr, productIds, addonIds });
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, garageId, selectedServicesIds]);

  // Fetch available slots when date changes or when date picker opens
  useEffect(() => {
    if (selectedDate && garageId && showDateTimePicker) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selected = new Date(selectedDate);
      selected.setHours(0, 0, 0, 0);

      if (selected >= today) {
        fetchAvailableSlots();
      } else {
        setAvailableSlots([]);
      }
    } else if (!showDateTimePicker) {
      setAvailableSlots([]);
    }
  }, [selectedDate, garageId, showDateTimePicker, fetchAvailableSlots]);

  const formatTimeDisplay = (time24) => {
    const [hours, minutes] = time24.split(':').map(Number);
    const hour12 = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    const ampm = hours >= 12 ? 'pm' : 'am';
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const getSlotStyles = (slot, isSelected) => {
    const now = new Date();
    const today = new Date().toDateString();
    const slotDate = selectedDate.toDateString();
    const isToday = slotDate === today;
    const [hours, minutes] = slot.time.split(':').map(Number);
    const slotTime = new Date();
    slotTime.setHours(hours, minutes, 0, 0);
    const isPast = isToday && slotTime < now;

    const baseStyles = {
      borderRadius: 2,
      textTransform: 'none',
      p: { xs: 0.5, sm: 1, md: 1 },
      minWidth: 'auto',
      width: '100%',
      height: { xs: '64px', sm: '68px', md: '72px' },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      transition: 'all 0.2s ease-in-out',
      border: '1px solid',
      boxSizing: 'border-box',
      overflow: 'hidden',
    };

    let colors;

    if (isPast) {
      colors = {
        bg: isSelected ? 'grey.500' : 'grey.50',
        border: isSelected ? 'grey.500' : 'grey.200',
        text: isSelected ? 'white' : 'grey.500',
        hover: isSelected ? 'grey.600' : 'grey.100',
      };
    } else {
      const statusColors = {
        available: {
          bg: isSelected ? '#2e7d32' : '#e8f5e8',
          border: isSelected ? '#2e7d32' : '#4caf50',
          text: isSelected ? 'white' : '#1b5e20',
          hover: isSelected ? '#1b5e20' : '#c8e6c9',
        },
        partially_booked: {
          bg: isSelected ? '#f57c00' : '#fff3e0',
          border: isSelected ? '#f57c00' : '#ff9800',
          text: isSelected ? 'white' : '#e65100',
          hover: isSelected ? '#e65100' : '#ffe0b2',
        },
        heavily_booked: {
          bg: isSelected ? '#d32f2f' : '#ffebee',
          border: isSelected ? '#d32f2f' : '#f44336',
          text: isSelected ? 'white' : '#b71c1c',
          hover: isSelected ? '#b71c1c' : '#ffcdd2',
        },
      };
      colors = statusColors[slot.availability_status] || statusColors.available;
    }

    return {
      ...baseStyles,
      backgroundColor: colors.bg,
      borderColor: colors.border,
      color: colors.text,
      opacity: isPast ? 0.6 : 1,
      cursor: isPast ? 'not-allowed' : 'pointer',
      '&:hover': {
        bgcolor: isPast ? colors.bg : colors.hover,
        transform: isPast ? 'none' : 'scale(1.02)',
        boxShadow: isPast ? 'none' : { xs: 1, sm: 2, md: 3 },
      },
      '&:disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
      },
    };
  };

  const handleVehicleCustomerSelect = (customer) => {
    setSelectedVehicleCustomer(customer);
    setShowVehicleCustomerPicker(false);
    setVehicleCustomerSearch('');
    setSearchPerformed(false);
    setSearching(false);
  };

  const handleAddCustomerAdded = (newCustomerData) => {
    const newCustomer = {
      id: newCustomerData.id,
      customer_name: newCustomerData.customer_name,
      mobile: newCustomerData.mobile,
      vehicle: newCustomerData.vehicle,
    };

    setSelectedVehicleCustomer(newCustomer);
    setGarageCustomers((prev) => [...prev, newCustomer]);
    setFilteredGarageCustomers((prev) => [...prev, newCustomer]);
  };

  const handleProductToggle = (product) => {
    setSelectedServices((prev) => {
      const isSelected = prev.some((p) => p.id === product.id);
      if (isSelected) {
        return prev.filter((p) => p.id !== product.id);
      } else {
        if (product.is_exclusive) {
          const filtered = prev.filter(
            (p) => !(p.category === product.category && p.is_exclusive)
          );
          return [...filtered, product];
        }
        return [...prev, product];
      }
    });
  };

  const handleAddonToggle = (addon) => {
    setSelectedServices((prev) => {
      const isSelected = prev.some((a) => a.id === addon.id);
      if (isSelected) {
        return prev.filter((a) => a.id !== addon.id);
      } else {
        return [...prev, addon];
      }
    });
  };

  const handleRemoveService = (serviceToRemove) => {
    setSelectedServices((prev) => prev.filter((service) => service.id !== serviceToRemove.id));
  };

  const othersProduct = availableProducts.find((p) => p.category?.toLowerCase() === 'others');

  const isProductService = (service) =>
    service.is_custom || availableProducts.some((p) => p.id === service.id);

  const isDuplicateCustomName = selectedServices.some(
    (s) => s.is_custom && s.custom_name.toLowerCase() === customServiceName.trim().toLowerCase()
  );

  const handleAddCustomService = () => {
    const name = customServiceName.trim();
    const price = parseFloat(customServicePrice);
    if (!othersProduct || !name || Number.isNaN(price) || price < 0) {
      return;
    }
    // Booking lines are unique per (product, custom name), so block duplicate names
    if (isDuplicateCustomName) {
      return;
    }

    setSelectedServices((prev) => [
      ...prev,
      {
        ...othersProduct,
        id: `others_${Date.now()}`, // Temporary ID for frontend
        product_id: othersProduct.product_id || othersProduct.id,
        name,
        custom_name: name,
        custom_price: price,
        is_free: false,
        category: 'Others',
        is_custom: true,
      },
    ]);
    setCustomServiceName('');
    setCustomServicePrice('');
  };

  const handleRefreshMot = async (customer) => {
    if (motRefreshStatus[customer.id] === 'loading') return;
    setMotRefreshStatus((prev) => ({ ...prev, [customer.id]: 'loading' }));
    try {
      const response = await apiService.refreshGarageCustomerMot(garageId, customer.id);
      const result = response.data ?? response;
      if (!result?.checked) {
        setMotRefreshStatus((prev) => ({ ...prev, [customer.id]: 'error' }));
        return;
      }
      const applyMot = (c) =>
        c.vehicle?.id === result.vehicle_id
          ? { ...c, vehicle: { ...c.vehicle, mot_expiry_date: result.mot_expiry_date } }
          : c;
      setGarageCustomers((prev) => prev.map(applyMot));
      setFilteredGarageCustomers((prev) => prev.map(applyMot));
      setSelectedVehicleCustomer((prev) => (prev ? applyMot(prev) : prev));
      setMotRefreshStatus((prev) => ({ ...prev, [customer.id]: undefined }));
    } catch (error) {
      console.error('Error refreshing MOT expiry:', error);
      setMotRefreshStatus((prev) => ({ ...prev, [customer.id]: 'error' }));
    }
  };

  const renderMotRefreshButton = (customer) => {
    const refreshStatus = motRefreshStatus[customer.id];
    return (
      <Tooltip
        title={
          refreshStatus === 'error'
            ? "Couldn't reach DVLA, showing saved date. Click to retry."
            : 'Get latest MOT expiry from DVLA'
        }
      >
        <span>
          <IconButton
            size="small"
            aria-label="Refresh MOT expiry"
            onClick={(e) => {
              e.stopPropagation();
              handleRefreshMot(customer);
            }}
            disabled={refreshStatus === 'loading'}
            sx={{ p: 0.5, color: refreshStatus === 'error' ? 'error.main' : 'text.secondary' }}
          >
            {refreshStatus === 'loading' ? (
              <CircularProgress size={14} />
            ) : (
              <RefreshIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  // Reflect saved name/mobile in the selected customer and search lists
  const handleCustomerContactSaved = (updated) => {
    const updateCustomer = (c) => (c.id === selectedVehicleCustomer.id ? { ...c, ...updated } : c);
    setSelectedVehicleCustomer((prev) => ({ ...prev, ...updated }));
    setGarageCustomers((prev) => prev.map(updateCustomer));
    setFilteredGarageCustomers((prev) => prev.map(updateCustomer));
  };

  const handleTimeSlotSelect = (slot) => {
    const newSlot = { ...slot };
    delete newSlot.is_original;
    setSelectedTimeSlot(newSlot);
    setShowDateTimePicker(false);
  };

  const handleCustomTimeSubmit = () => {
    setCustomTimeError('');

    if (!customTimeInput || !customTimeInput.match(/^\d{2}:\d{2}$/)) {
      setCustomTimeError('Please enter a valid time in HH:MM format');
      return;
    }

    const [hours, minutes] = customTimeInput.split(':').map(Number);
    const customTime = new Date();
    customTime.setHours(hours, minutes, 0, 0);

    const dayName = selectedDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();

    const defaultOpeningHours = {
      monday: { open: '09:00', close: '17:00' },
      tuesday: { open: '09:00', close: '17:00' },
      wednesday: { open: '09:00', close: '17:00' },
      thursday: { open: '09:00', close: '17:00' },
      friday: { open: '09:00', close: '17:00' },
      saturday: { open: '09:00', close: '17:00' },
      sunday: null,
    };

    const dayHours = selectedGarage?.opening_hours?.[dayName] || defaultOpeningHours[dayName];

    if (!dayHours) {
      setCustomTimeError('Garage is closed on this day');
      return;
    }

    const [openHours, openMinutes] = dayHours.open.split(':').map(Number);
    const [closeHours, closeMinutes] = dayHours.close.split(':').map(Number);

    const openTime = new Date();
    openTime.setHours(openHours, openMinutes, 0, 0);

    const closeTime = new Date();
    closeTime.setHours(closeHours, closeMinutes, 0, 0);

    if (customTime < openTime || customTime >= closeTime) {
      setCustomTimeError(`Please enter a time between ${dayHours.open} and ${dayHours.close}`);
      return;
    }

    const lunchHours = selectedGarage?.lunch_hours;
    if (lunchHours?.enabled) {
      const [lunchStartHours, lunchStartMinutes] = lunchHours.start.split(':').map(Number);
      const [lunchEndHours, lunchEndMinutes] = lunchHours.end.split(':').map(Number);

      const lunchStart = new Date();
      lunchStart.setHours(lunchStartHours, lunchStartMinutes, 0, 0);

      const lunchEnd = new Date();
      lunchEnd.setHours(lunchEndHours, lunchEndMinutes, 0, 0);

      if (customTime >= lunchStart && customTime < lunchEnd) {
        setCustomTimeError(`Cannot book during lunch hours (${lunchHours.start} - ${lunchHours.end})`);
        return;
      }
    }

    const now = new Date();
    const today = new Date().toDateString();
    const selectedDateStr = selectedDate.toDateString();

    if (selectedDateStr === today && customTime <= now) {
      setCustomTimeError('Cannot book appointments in the past');
      return;
    }

    const customSlot = {
      time: customTimeInput,
      display: formatTimeDisplay(customTimeInput),
      available: true,
      slot_id: `${selectedDate.toISOString().split('T')[0]}_${customTimeInput.replace(':', '')}`,
      duration_minutes: selectedServices.reduce(
        (total, service) => total + (service.duration_minutes || 60),
        0
      ),
      price: 0,
      booking_count: 0,
      availability_status: 'available',
      is_custom: true,
    };
    setSelectedTimeSlot(customSlot);
    setShowCustomTimeInput(false);
    setCustomTimeInput('');
    setCustomTimeError('');
    setShowDateTimePicker(false);
  };

  const handleSubmit = async () => {
    if (!selectedVehicleCustomer || !selectedServices.length || !selectedTimeSlot) {
      return;
    }

    setSubmitLoading(true);
    try {
      const selectedProducts = selectedServices.filter(isProductService);
      const selectedAddons = selectedServices.filter((service) =>
        availableAddons.some((addon) => addon.id === service.id)
      );

      if (rescheduleMode && rescheduleBookingId) {
        const updateData = {
          booking_date: getLocalDateString(selectedDate),
          booking_time: selectedTimeSlot.time,
          notes: rescheduleBookingData?.booking
            ? `Appointment rescheduled by Garage Admin from ${rescheduleBookingData.booking.booking_date} ${rescheduleBookingData.booking.booking_time}`
            : 'Appointment rescheduled by Garage Admin',
        };

        await apiService.updateBooking(rescheduleBookingId, updateData);

        if (onSuccess) {
          onSuccess();
        }
        return;
      } else {
        const appointmentData = {
          garage_id: garageId,
          vehicle_id: selectedVehicleCustomer.vehicle.id,
          customer_id: selectedVehicleCustomer.id,
          products: selectedProducts.map((product) => ({
            product_id: product.is_custom ? product.product_id : product.id,
            price: Math.round(
              (product.is_custom
                ? product.custom_price
                : product.custom_price || product.default_price || 0) * 100
            ),
            ...(product.is_custom && { custom_name: product.custom_name }),
          })),
          addons: selectedAddons.map((addon) => ({
            addon_id: addon.id,
            price: Math.round((addon.custom_price || addon.default_price || 0) * 100),
          })),
          booking_date: getLocalDateString(selectedDate),
          booking_time: selectedTimeSlot.time,
          send_notifications: true,
          booking_type: 'admin',
          notes: 'Appointment created by Garage Admin',
        };

        await apiService.createBooking(appointmentData);
      }

      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (error) {
      console.error(`Error ${rescheduleMode ? 'rescheduling' : 'creating'} appointment:`, error);

      let errorMessage = `Failed to ${rescheduleMode ? 'reschedule' : 'create'} appointment. Please try again.`;
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.detail) {
        errorMessage = error.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }

      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleClose = () => {
    resetFormState();
    setVehicleCustomerSearch('');
    setServiceSearchTerm('');
    setSearchPerformed(false);
    setSearching(false);
    setGarageCustomers([]);
    setFilteredGarageCustomers([]);
    onClose();
  };

  // Filter services based on search term and exclude 'Others' category
  const filteredServices = React.useMemo(() => {
    const allServices = [...availableProducts, ...availableAddons].filter(
      (service) => service.category?.toLowerCase() !== 'others'
    );
    if (!serviceSearchTerm.trim()) {
      return allServices;
    }
    return allServices.filter(
      (service) =>
        service.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()) ||
        (service.category &&
          service.category.toLowerCase().includes(serviceSearchTerm.toLowerCase()))
    );
  }, [availableProducts, availableAddons, serviceSearchTerm]);

  const isFormValid = selectedVehicleCustomer && selectedServices.length > 0 && selectedTimeSlot;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, md: 3 },
          m: { xs: 0, md: 2 },
          height: '100%',
          maxHeight: { xs: '100vh', md: '800px' },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          fontSize: '1.5rem',
          fontWeight: 600,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
            {rescheduleMode ? 'Reschedule Appointment' : 'New Appointment'}
          </Typography>
          {(prefilledDate || prefilledTime) && !rescheduleMode && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
              {prefilledDate && prefilledDate.toLocaleDateString('en-GB', { 
                weekday: 'long', 
                day: 'numeric', 
                month: 'short', 
                year: 'numeric' 
              })}
              {prefilledDate && prefilledTime && ' at '}
              {prefilledTime && (() => {
                const [hours, minutes] = prefilledTime.split(':');
                const hour12 = parseInt(hours) > 12 ? parseInt(hours) - 12 : parseInt(hours) === 0 ? 12 : parseInt(hours);
                const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
                return `${hour12}:${minutes} ${ampm}`;
              })()}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 4,
          pb: 3,
          px: 3,
          flex: 1,
          overflow: 'auto',
        }}
      >
        {rescheduleMode && loading && !rescheduleBookingData ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              textAlign: 'center',
            }}
          >
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Loading appointment details...
            </Typography>
          </Box>
        ) : (
          <Stack spacing={4} sx={{ mt: 2 }}>
            {/* Customer Selection */}
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight="500"
                color="text.secondary"
                sx={{ mb: 2, fontSize: '1.125rem' }}
              >
                Customer
              </Typography>

              {!selectedVehicleCustomer ? (
                <Box sx={{ position: 'relative' }} ref={customerInputRef}>
                  <OutlinedInput
                    fullWidth
                    placeholder="Select Customer"
                    readOnly
                    onClick={() => {
                      setShowVehicleCustomerPicker(true);
                      setTimeout(() => {
                        scrollToTop(customerInputRef);
                        // Auto-focus the search input
                        if (customerSearchInputRef.current) {
                          customerSearchInputRef.current.focus();
                        }
                      }, 100);
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <ExpandMoreIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    }
                    sx={{
                      cursor: 'pointer',
                      '& .MuiOutlinedInput-input': {
                        cursor: 'pointer',
                      },
                    }}
                  />

                  {/* Vehicle Customer Picker */}
                  {showVehicleCustomerPicker && (
                    <Card
                      ref={vehicleCustomerPickerRef}
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        mt: 1,
                        maxHeight: 400,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="e.g. AB12 CDE or John Smith"
                          value={vehicleCustomerSearch}
                          inputRef={customerSearchInputRef}
                          onChange={(e) => {
                            setVehicleCustomerSearch(e.target.value);
                            if (searchPerformed) {
                              setSearchPerformed(false);
                              setGarageCustomers([]);
                              setFilteredGarageCustomers([]);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              searchGarageCustomers();
                            }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button
                                  size="small"
                                  onClick={searchGarageCustomers}
                                  disabled={!vehicleCustomerSearch.trim()}
                                  sx={{ minWidth: 'auto', p: 0.5 }}
                                >
                                  <SearchIcon sx={{ fontSize: 18 }} />
                                </Button>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>

                      {/* Add New Customer Link */}
                      <Box
                        sx={{
                          p: 2,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Button
                          startIcon={<AddIcon sx={{ fontSize: 18, fontWeight: 'bold' }} />}
                          onClick={() => {
                            setAddCustomerModalOpen(true);
                            setShowVehicleCustomerPicker(false);
                          }}
                          sx={{
                            color: 'primary.main',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            p: 0,
                            minWidth: 'auto',
                            justifyContent: 'flex-start',
                            gap: 1,
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: 'primary.dark',
                              textDecoration: 'underline',
                              '& .MuiSvgIcon-root': {
                                transform: 'scale(1.1)',
                              },
                            },
                          }}
                        >
                          New Customer
                        </Button>
                      </Box>

                      <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 300 }}>
                        {searching ? (
                          <Box sx={{ p: 3, textAlign: 'center' }}>
                            <CircularProgress size={24} sx={{ mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              Searching...
                            </Typography>
                          </Box>
                        ) : filteredGarageCustomers.length > 0 ? (
                          filteredGarageCustomers.map((customer) => (
                            <Box
                              key={customer.id}
                              onClick={() => handleVehicleCustomerSelect(customer)}
                              sx={{
                                p: { xs: 2, sm: 2.5 },
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                  transform: 'translateX(2px)',
                                },
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:last-child': {
                                  borderBottom: 'none',
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: { xs: 1.5, sm: 2 },
                                  flexDirection: 'column',
                                }}
                              >
                                {/* Registration and MOT */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: { xs: 0.8, sm: 1.5 },
                                    width: '100%',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: { xs: 1, sm: 1.5 },
                                      flexShrink: 0,
                                    }}
                                  >
                                    <CarIcon
                                      sx={{
                                        color: 'primary.main',
                                        fontSize: { xs: 20, sm: 24 },
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        backgroundColor: '#FEDE00',
                                        border: '2px solid #000000',
                                        borderRadius: 1,
                                        px: { xs: 0.8, sm: 1.5 },
                                        py: { xs: 0.3, sm: 0.5 },
                                        minWidth: { xs: '60px', sm: '75px' },
                                        textAlign: 'center',
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          fontWeight: 900,
                                          fontSize: { xs: '0.65rem', sm: '0.85rem' },
                                          color: '#000000',
                                          letterSpacing: { xs: '0.2px', sm: '0.5px' },
                                          fontFamily: 'monospace',
                                          textTransform: 'uppercase',
                                          lineHeight: 1.2,
                                        }}
                                      >
                                        {customer.vehicle.registration_number}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                                    {customer.vehicle.mot_expiry_date && (
                                      <Chip
                                        label={`MOT expires on ${new Date(
                                          customer.vehicle.mot_expiry_date
                                        ).toLocaleDateString('en-GB')}`}
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        sx={{
                                          fontSize: '0.7rem',
                                          height: 24,
                                          backgroundColor: 'transparent',
                                        }}
                                      />
                                    )}
                                    {renderMotRefreshButton(customer)}
                                  </Box>
                                </Box>

                                {/* Vehicle Details */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                    width: '100%',
                                  }}
                                >
                                  {[
                                    customer.vehicle.make,
                                    customer.vehicle.model,
                                    customer.vehicle.year,
                                    customer.vehicle.colour,
                                  ]
                                    .filter(Boolean)
                                    .map((detail, index) => (
                                      <Chip
                                        key={index}
                                        label={detail}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          fontSize: '0.7rem',
                                          height: 24,
                                          backgroundColor: 'transparent',
                                          borderColor: 'divider',
                                          color: 'text.secondary',
                                        }}
                                      />
                                    ))}
                                </Box>

                                {/* Customer Info */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    width: '100%',
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    <PersonIcon sx={{ fontSize: 14 }} />
                                    {customer.customer_name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    <PhoneIcon sx={{ fontSize: 14 }} />
                                    {customer.mobile}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          ))
                        ) : searchPerformed && vehicleCustomerSearch.trim() ? (
                          <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              No customers found
                            </Typography>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                setAddCustomerModalOpen(true);
                                setShowVehicleCustomerPicker(false);
                              }}
                              sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                              }}
                            >
                              Add New Customer
                            </Button>
                          </Box>
                        ) : (
                          <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              Enter customer name or vehicle registration to search
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Card>
                  )}
                </Box>
              ) : (
                <Box>
                  <Box
                    sx={{
                      p: { xs: 2.5, sm: 3 },
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'primary.main',
                      boxShadow: 1,
                    }}
                  >
                    {/* Vehicle Registration & Icon & MOT */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: { xs: 1, sm: 2 },
                        mb: 2,
                        width: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: { xs: 1.5, sm: 2 },
                          flexShrink: 0,
                        }}
                      >
                        <CarIcon
                          sx={{
                            color: 'primary.main',
                            fontSize: { xs: 22, sm: 24 },
                            flexShrink: 0,
                          }}
                        />
                        <Box
                          sx={{
                            backgroundColor: '#FEDE00',
                            border: '3px solid #000000',
                            borderRadius: 1.5,
                            px: { xs: 1, sm: 2 },
                            py: { xs: 0.5, sm: 0.75 },
                            minWidth: { xs: '70px', sm: '90px' },
                            textAlign: 'center',
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 900,
                              fontSize: { xs: '0.75rem', sm: '1.1rem' },
                              color: '#000000',
                              letterSpacing: { xs: '0.3px', sm: '1px' },
                              fontFamily: 'monospace',
                              textTransform: 'uppercase',
                              lineHeight: 1.2,
                            }}
                          >
                            {selectedVehicleCustomer.vehicle.registration_number}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                        {selectedVehicleCustomer.vehicle.mot_expiry_date && (
                          <Chip
                            label={`MOT expires on ${new Date(
                              selectedVehicleCustomer.vehicle.mot_expiry_date
                            ).toLocaleDateString('en-GB')}`}
                            color="error"
                            variant="outlined"
                            size="small"
                            sx={{
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              height: { xs: 26, sm: 28 },
                              fontWeight: 500,
                            }}
                          />
                        )}
                        {!rescheduleMode && renderMotRefreshButton(selectedVehicleCustomer)}
                      </Box>
                    </Box>

                    {/* Vehicle Details */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      {[
                        selectedVehicleCustomer.vehicle.make,
                        selectedVehicleCustomer.vehicle.model,
                        selectedVehicleCustomer.vehicle.year,
                        selectedVehicleCustomer.vehicle.colour,
                      ]
                        .filter(Boolean)
                        .map((detail, index) => (
                          <Chip
                            key={index}
                            label={detail}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              height: { xs: 26, sm: 28 },
                              backgroundColor: 'action.selected',
                              borderColor: 'divider',
                            }}
                          />
                        ))}
                    </Box>

                    {/* Customer Info */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                        <CustomerContactEditor
                          customer={selectedVehicleCustomer}
                          garageId={garageId}
                          editable={!rescheduleMode}
                          onSaved={handleCustomerContactSaved}
                          onEditingChange={setEditingContact}
                          nameTypographyProps={{ sx: { fontSize: '0.875rem' } }}
                          mobileTypographyProps={{ sx: { fontSize: '0.875rem' } }}
                        />

                        {!rescheduleMode && !editingContact && (
                          <Button
                            onClick={() => {
                              setSelectedVehicleCustomer(null);
                              setVehicleCustomerSearch('');
                              setSearchPerformed(false);
                              setSearching(false);
                            }}
                            variant="text"
                            size="small"
                            sx={{
                              color: 'primary.main',
                              textTransform: 'none',
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              textDecoration: 'underline',
                              minWidth: 'auto',
                              p: 0.5,
                              '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline',
                                color: 'primary.dark',
                              },
                            }}
                          >
                            Change
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Services Selection */}
            {selectedVehicleCustomer && (
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight="500"
                  color="text.secondary"
                  sx={{ mb: 2, fontSize: '1.125rem' }}
                >
                  Services
                </Typography>

                {availableProducts.length === 0 && availableAddons.length === 0 ? (
                  <Box
                    sx={{
                      p: 3,
                      bgcolor: 'warning.50',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'warning.200',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body1" color="warning.dark" sx={{ mb: 1 }}>
                      No services available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Please configure garage products and add-ons in garage settings to enable service
                      selection.
                    </Typography>
                  </Box>
                ) : (
                  <Box ref={servicesInputRef}>
                    <FormControl fullWidth>
                      <OutlinedInput
                        placeholder={selectedServices.length > 0 ? '' : 'Select Services'}
                        readOnly
                        onClick={() => {
                          setShowServicesPicker(true);
                          setTimeout(() => {
                            scrollToTop(servicesInputRef);
                          }, 100);
                        }}
                        value=""
                        startAdornment={
                          selectedServices.length > 0 ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                                mr: 1,
                                width: '100%',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                              }}
                            >
                              {selectedServices.map((service) => {
                                const isProduct = isProductService(service);
                                return (
                                  <Chip
                                    key={service.id}
                                    label={
                                      service.is_custom
                                        ? `${service.name} (£${service.custom_price.toFixed(2)})`
                                        : service.name
                                    }
                                    size="small"
                                    color={isProduct ? 'primary' : 'success'}
                                    onDelete={(e) => {
                                      e.stopPropagation();
                                      handleRemoveService(service);
                                    }}
                                    sx={{
                                      height: 28,
                                      fontSize: '0.75rem',
                                      flex: 1,
                                      minWidth: '80px',
                                      maxWidth: '200px',
                                      justifyContent: 'flex-start',
                                      '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        textAlign: 'left',
                                        paddingLeft: '8px',
                                        paddingRight: '4px',
                                      },
                                      '& .MuiChip-deleteIcon': {
                                        fontSize: '16px',
                                        marginLeft: 'auto',
                                      },
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          ) : null
                        }
                        endAdornment={
                          <InputAdornment position="end">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {selectedServices.length > 0 && (
                                <Button
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedServices([]);
                                    setServiceSearchTerm('');
                                  }}
                                  sx={{
                                    textTransform: 'none',
                                    minWidth: 'auto',
                                    fontSize: '0.75rem',
                                    color: 'text.secondary',
                                    p: 0.5,
                                    '&:hover': {
                                      bgcolor: 'action.hover',
                                    },
                                  }}
                                >
                                  Clear All
                                </Button>
                              )}
                              <ExpandMoreIcon
                                sx={{
                                  transform: showServicesPicker ? 'rotate(180deg)' : 'none',
                                  transition: 'transform 0.2s ease',
                                }}
                              />
                            </Box>
                          </InputAdornment>
                        }
                        sx={{
                          borderRadius: 2,
                          cursor: 'pointer',
                          minHeight: selectedServices.length > 0 ? 'auto' : '56px',
                          alignItems: 'center',
                          py: selectedServices.length > 0 ? 1 : 1.5,
                          '& .MuiOutlinedInput-input': {
                            textAlign: 'left',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: showServicesPicker ? 'primary.main' : 'divider',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                        }}
                      />
                    </FormControl>

                    {/* Services Picker Widget */}
                    {showServicesPicker && (
                      <Box
                        ref={servicesPickerRef}
                        sx={{
                          mt: 1,
                          p: 0,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          boxShadow: 3,
                          position: 'relative',
                          zIndex: 1000,
                        }}
                      >
                        {/* Search Bar */}
                        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <TextField
                            fullWidth
                            placeholder="Enter to Search"
                            value={serviceSearchTerm}
                            inputRef={servicesSearchInputRef}
                            onChange={(e) => setServiceSearchTerm(e.target.value)}
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                              },
                            }}
                          />
                        </Box>

                        {/* Services List */}
                        <Box
                          sx={{
                            maxHeight: { xs: 250, md: 300 },
                            overflowY: 'auto',
                          }}
                        >
                          {filteredServices.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                              <Typography variant="body2" color="text.secondary">
                                {serviceSearchTerm
                                  ? `No services found matching "${serviceSearchTerm}"`
                                  : 'No services available'}
                              </Typography>
                            </Box>
                          ) : (
                            filteredServices.map((service, index) => {
                              const isProduct = availableProducts.some((p) => p.id === service.id);
                              const isSelected = selectedServices.some((s) => s.id === service.id);
                              const toggleHandler = isProduct ? handleProductToggle : handleAddonToggle;

                              return (
                                <Box key={service.id}>
                                  <Box
                                    sx={{
                                      p: 2.5,
                                      cursor: 'pointer',
                                      bgcolor: isSelected
                                        ? isProduct
                                          ? 'primary.50'
                                          : 'success.50'
                                        : 'transparent',
                                      '&:hover': {
                                        bgcolor: isSelected
                                          ? isProduct
                                            ? 'primary.100'
                                            : 'success.100'
                                          : 'action.hover',
                                      },
                                      transition: 'background-color 0.2s ease',
                                    }}
                                    onClick={() => toggleHandler(service)}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      {/* Category Legend Style */}
                                      {(() => {
                                        const categoryStyle = getCategoryStyle(service, isProduct);
                                        return categoryStyle ? (
                                          <Box
                                            sx={{
                                              width: 24,
                                              height: 24,
                                              borderRadius: '50%',
                                              bgcolor: categoryStyle.color,
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              flexShrink: 0
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                color: 'white',
                                                textTransform: 'uppercase',
                                                lineHeight: 1
                                              }}
                                            >
                                              {categoryStyle.letter}
                                            </Typography>
                                          </Box>
                                        ) : null;
                                      })()}

                                      {/* Service Details */}
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5 }}>
                                          {service.name}
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            flexWrap: 'wrap',
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            color={service.is_free ? 'success.main' : 'primary.main'}
                                            fontWeight="600"
                                          >
                                            {service.is_free
                                              ? 'Free'
                                              : `£${service.custom_price || service.default_price}`}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {service.custom_duration || service.duration_minutes || 0} mins
                                          </Typography>
                                        </Box>
                                      </Box>

                                      {/* Checkbox */}
                                      <Box onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                          checked={isSelected}
                                          onChange={() => toggleHandler(service)}
                                          size="small"
                                          sx={{
                                            color: isProduct ? 'primary.main' : 'success.main',
                                            '&.Mui-checked': {
                                              color: isProduct ? 'primary.main' : 'success.main',
                                            },
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                  </Box>
                                  {index < filteredServices.length - 1 && <Divider sx={{ mx: 2.5 }} />}
                                </Box>
                              );
                            })
                          )}
                        </Box>

                        {/* Add Custom Service */}
                        {othersProduct && (
                          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" fontWeight="600" sx={{ mb: 1 }}>
                              Add Custom Service
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: { xs: 'stretch', sm: 'center' },
                                flexDirection: { xs: 'column', sm: 'row' },
                              }}
                            >
                              <TextField
                                size="small"
                                label="Service Name"
                                placeholder="Enter custom service name"
                                value={customServiceName}
                                onChange={(e) => setCustomServiceName(e.target.value)}
                                error={isDuplicateCustomName}
                                helperText={isDuplicateCustomName ? 'Already added' : undefined}
                                inputProps={{ maxLength: 255 }}
                                sx={{ flex: 1 }}
                              />
                              <TextField
                                size="small"
                                label="Price (£)"
                                placeholder="0.00"
                                type="number"
                                value={customServicePrice}
                                onChange={(e) => setCustomServicePrice(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleAddCustomService();
                                }}
                                inputProps={{ step: 0.01, min: 0 }}
                                sx={{ width: { xs: '100%', sm: 120 } }}
                              />
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={handleAddCustomService}
                                disabled={
                                  !customServiceName.trim() ||
                                  isDuplicateCustomName ||
                                  customServicePrice === '' ||
                                  Number.isNaN(parseFloat(customServicePrice)) ||
                                  parseFloat(customServicePrice) < 0
                                }
                                sx={{ textTransform: 'none', height: 40 }}
                              >
                                Add
                              </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Add a service that isn't in the list, with its own price
                            </Typography>
                          </Box>
                        )}

                        {/* Action Buttons */}
                        <Box
                          sx={{
                            p: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {selectedServices.length} selected
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              onClick={() => {
                                setSelectedServices([]);
                                setServiceSearchTerm('');
                              }}
                              sx={{ textTransform: 'none' }}
                            >
                              Clear All
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                setShowServicesPicker(false);
                                setServiceSearchTerm('');
                              }}
                              sx={{ textTransform: 'none' }}
                            >
                              Done
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Date & Time Selection */}
            {selectedVehicleCustomer && selectedServices.length > 0 && (
              <Box ref={dateTimeInputRef}>
                <Typography
                  variant="subtitle1"
                  fontWeight="500"
                  color="text.secondary"
                  sx={{ mb: 2, fontSize: '1.125rem' }}
                >
                  Date & Time
                </Typography>

                {!selectedTimeSlot ? (
                  <FormControl fullWidth>
                    <OutlinedInput
                      value=""
                      placeholder="Select Date & Time"
                      readOnly
                      startAdornment={
                        <InputAdornment position="start">
                          <CalendarIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      }
                      endAdornment={
                        <InputAdornment position="end">
                          <ExpandMoreIcon />
                        </InputAdornment>
                      }
                      sx={{
                        borderRadius: 2,
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const currentSelectedDate = new Date(selectedDate);
                        currentSelectedDate.setHours(0, 0, 0, 0);

                        if (currentSelectedDate < today) {
                          setSelectedDate(new Date());
                        }
                        setShowDateTimePicker(true);
                        setTimeout(() => scrollToTop(dateTimeInputRef), 100);
                      }}
                    />
                  </FormControl>
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'background.default',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: rescheduleMode && selectedTimeSlot?.is_original ? 'warning.main' : 'primary.main',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon
                          sx={{
                            color: rescheduleMode && selectedTimeSlot?.is_original ? 'warning.main' : 'primary.main',
                          }}
                        />
                        <Typography variant="body1" fontWeight="500">
                          {selectedDate.toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}{' '}
                          at {selectedTimeSlot.display}
                        </Typography>
                      </Box>
                      {rescheduleMode && selectedTimeSlot?.is_original && (
                        <Typography variant="caption" color="warning.main" sx={{ ml: 3, fontSize: '0.75rem' }}>
                          Original booking time
                        </Typography>
                      )}
                    </Box>
                    <Button
                      onClick={() => {
                        setSelectedTimeSlot(null);
                        setShowDateTimePicker(true);
                        setTimeout(() => scrollToTop(dateTimeInputRef), 100);
                      }}
                      variant="text"
                      size="small"
                      sx={{
                        color: 'primary.main',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        textDecoration: 'underline',
                        minWidth: 'auto',
                        p: 0.5,
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline',
                          color: 'primary.dark',
                        },
                      }}
                    >
                      Change
                    </Button>
                  </Box>
                )}

                {/* Inline Date Time Picker */}
                {showDateTimePicker && (
                  <Box
                    ref={dateTimePickerRef}
                    sx={{
                      mt: 2,
                      p: { xs: 2, sm: 3 },
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 3, md: 4 },
                        width: '100%',
                      }}
                    >
                      {/* Calendar */}
                      <Box
                        sx={{
                          flex: { xs: 'none', md: 1 },
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                            width: '100%',
                          }}
                        >
                          <IconButton
                            onClick={() => {
                              if (loading) return;
                              const newDate = new Date(selectedDate);
                              const currentMonth = new Date().getMonth();
                              const currentYear = new Date().getFullYear();
                              const newMonth = newDate.getMonth() - 1;
                              const newYear = newDate.getFullYear();

                              if (newYear > currentYear || (newYear === currentYear && newMonth >= currentMonth)) {
                                newDate.setMonth(newMonth);
                                newDate.setMonth(newDate.getMonth() + 1, 0);
                                setSelectedDate(newDate);
                              }
                            }}
                            size="small"
                            disabled={(() => {
                              const currentMonth = new Date().getMonth();
                              const currentYear = new Date().getFullYear();
                              const selectedMonth = selectedDate.getMonth();
                              const selectedYear = selectedDate.getFullYear();

                              return selectedYear === currentYear && selectedMonth <= currentMonth;
                            })()}
                            sx={{
                              pointerEvents: loading ? 'none' : 'auto',
                            }}
                          >
                            <ChevronLeftIcon />
                          </IconButton>
                          <Typography variant="subtitle1" fontWeight="600" sx={{ fontSize: '1.125rem' }}>
                            {selectedDate.toLocaleDateString('en-GB', {
                              month: 'short',
                              year: 'numeric',
                            })}
                          </Typography>
                          <IconButton
                            onClick={() => {
                              const newDate = new Date(selectedDate);
                              newDate.setMonth(newDate.getMonth() + 1);
                              newDate.setDate(1);
                              setSelectedDate(newDate);
                            }}
                            size="small"
                            disabled={loading}
                          >
                            <ChevronRightIcon />
                          </IconButton>
                        </Box>

                        {/* Calendar Grid */}
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, 1fr)',
                            gap: { xs: 0.5, sm: 1 },
                            mb: 2,
                            width: '100%',
                          }}
                        >
                          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
                            <Typography
                              key={`day-${index}`}
                              variant="caption"
                              color="text.secondary"
                              sx={{ textAlign: 'center', py: 1, fontWeight: 500 }}
                            >
                              {day}
                            </Typography>
                          ))}
                        </Box>

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, 1fr)',
                            gap: { xs: 0.5, sm: 1 },
                            width: '100%',
                          }}
                        >
                          {(() => {
                            const year = selectedDate.getFullYear();
                            const month = selectedDate.getMonth();
                            const firstDay = new Date(year, month, 1);
                            const lastDay = new Date(year, month + 1, 0);

                            const startDayOfWeek = (firstDay.getDay() + 6) % 7;

                            const calendarDays = [];

                            for (let i = 0; i < startDayOfWeek; i++) {
                              calendarDays.push(
                                <Box
                                  key={`empty-${i}`}
                                  sx={{
                                    width: { xs: 32, sm: 40 },
                                    height: { xs: 32, sm: 40 },
                                  }}
                                />
                              );
                            }

                            for (let day = 1; day <= lastDay.getDate(); day++) {
                              const currentDate = new Date(year, month, day);
                              const isSelected = currentDate.toDateString() === selectedDate.toDateString();
                              const isToday = currentDate.toDateString() === new Date().toDateString();
                              const isPast = currentDate.getTime() < new Date().setHours(0, 0, 0, 0);

                              calendarDays.push(
                                <Button
                                  key={`${year}-${month}-${day}`}
                                  onClick={() => {
                                    if (!isPast) {
                                      setSelectedDate(currentDate);
                                    }
                                  }}
                                  disabled={isPast || loading}
                                  sx={{
                                    minWidth: 'auto',
                                    width: { xs: 32, sm: 40 },
                                    height: { xs: 32, sm: 40 },
                                    borderRadius: isSelected ? '50%' : 1,
                                    fontSize: '0.875rem',
                                    p: 0,
                                    color: isSelected ? 'primary.contrastText' : 'text.primary',
                                    bgcolor: isSelected ? 'primary.main' : 'transparent',
                                    border: isToday && !isSelected ? '1px solid' : 'none',
                                    borderColor: 'primary.main',
                                    fontWeight: isSelected ? 700 : 500,
                                    '&:hover': {
                                      bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                                      transform: 'scale(1.05)',
                                      boxShadow: isSelected
                                        ? '0 4px 12px rgba(25, 118, 210, 0.25)'
                                        : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                    },
                                    '&.Mui-disabled': {
                                      color: 'text.disabled',
                                      bgcolor: 'transparent',
                                    },
                                    transition: 'all 0.2s ease-in-out',
                                  }}
                                >
                                  {day}
                                </Button>
                              );
                            }

                            return calendarDays;
                          })()}
                        </Box>
                      </Box>

                      {/* Available Slots */}
                      <Box
                        sx={{
                          flex: { xs: 'none', md: 1 },
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight="600"
                          sx={{ mb: 1, fontSize: '1.125rem', textAlign: 'center', width: '100%' }}
                        >
                          Available Slots
                        </Typography>

                        {loading ? (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: 150,
                              gap: 2,
                              width: '100%',
                            }}
                          >
                            <CircularProgress size={32} />
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                              Loading available slots...
                            </Typography>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: {
                                xs: 'repeat(3, 1fr)',
                                sm: 'repeat(4, 1fr)',
                                md: 'repeat(3, 1fr)',
                                lg: 'repeat(4, 1fr)',
                              },
                              gap: { xs: 1, sm: 1.5, md: 1.5 },
                              minHeight: { xs: 250, md: 300 },
                              maxHeight: { xs: 250, md: 300 },
                              overflowY: 'auto',
                              overflowX: 'hidden',
                              width: '100%',
                              justifyItems: 'stretch',
                              p: { xs: 0.5, sm: 1, md: 1 },
                              boxSizing: 'border-box',
                            }}
                          >
                            {availableSlots.length === 0 ? (
                              <Box
                                sx={{
                                  gridColumn: '1 / -1',
                                  textAlign: 'center',
                                  py: 4,
                                }}
                              >
                                <Typography variant="body2" color="text.secondary">
                                  No available slots for this date
                                </Typography>
                              </Box>
                            ) : (
                              availableSlots.map((slot) => {
                                const isSelected = selectedTimeSlot?.time === slot.time;
                                return (
                                  <Button
                                    key={slot.time}
                                    variant={isSelected ? 'contained' : 'outlined'}
                                    onClick={() => {
                                      handleTimeSlotSelect(slot);
                                      setShowDateTimePicker(false);
                                    }}
                                    disabled={loading}
                                    sx={getSlotStyles(slot, isSelected)}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                        height: '100%',
                                        p: { xs: 0.75, sm: 1, md: 1.25 },
                                        overflow: 'hidden',
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                                          textAlign: 'center',
                                          lineHeight: 1.1,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          flexShrink: 0,
                                          mb: 0.5,
                                        }}
                                      >
                                        {slot.display}
                                      </Typography>
                                      <Box
                                        sx={{
                                          minWidth: { xs: 20, sm: 22, md: 24 },
                                          height: { xs: 20, sm: 22, md: 24 },
                                          borderRadius: '50%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                                          fontWeight: 700,
                                          border: '2px solid',
                                          boxShadow: isSelected ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
                                          transition: 'all 0.2s ease-in-out',
                                          flexShrink: 0,
                                          bgcolor: (() => {
                                            const now = new Date();
                                            const today = new Date().toDateString();
                                            const slotDate = selectedDate.toDateString();
                                            const isToday = slotDate === today;
                                            const [hours, minutes] = slot.time.split(':').map(Number);
                                            const slotTime = new Date();
                                            slotTime.setHours(hours, minutes, 0, 0);
                                            const isPast = isToday && slotTime < now;

                                            if (isPast) return '#bdbdbd';
                                            if (isSelected) return 'rgba(255,255,255,0.9)';
                                            if (slot.booking_count === 0) return '#4caf50';
                                            if (slot.booking_count === 1) return '#ff9800';
                                            return '#f44336';
                                          })(),
                                          borderColor: (() => {
                                            const now = new Date();
                                            const today = new Date().toDateString();
                                            const slotDate = selectedDate.toDateString();
                                            const isToday = slotDate === today;
                                            const [hours, minutes] = slot.time.split(':').map(Number);
                                            const slotTime = new Date();
                                            slotTime.setHours(hours, minutes, 0, 0);
                                            const isPast = isToday && slotTime < now;

                                            if (isPast) return '#9e9e9e';
                                            if (isSelected) return 'rgba(255,255,255,0.5)';
                                            if (slot.booking_count === 0) return '#2e7d32';
                                            if (slot.booking_count === 1) return '#f57c00';
                                            return '#d32f2f';
                                          })(),
                                          color: (() => {
                                            const now = new Date();
                                            const today = new Date().toDateString();
                                            const slotDate = selectedDate.toDateString();
                                            const isToday = slotDate === today;
                                            const [hours, minutes] = slot.time.split(':').map(Number);
                                            const slotTime = new Date();
                                            slotTime.setHours(hours, minutes, 0, 0);
                                            const isPast = isToday && slotTime < now;

                                            if (isPast) return 'white';
                                            if (isSelected) return '#1976d2';
                                            return 'white';
                                          })(),
                                        }}
                                      >
                                        {slot.booking_count}
                                      </Box>
                                    </Box>
                                  </Button>
                                );
                              })
                            )}
                          </Box>
                        )}

                        {/* Custom Time Input */}
                        <Box
                          sx={{
                            mt: 2,
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {!showCustomTimeInput ? (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setShowCustomTimeInput(true)}
                              sx={{
                                borderStyle: 'dashed',
                                borderColor: 'primary.main',
                                color: 'primary.main',
                                '&:hover': {
                                  borderStyle: 'dashed',
                                  borderColor: 'primary.dark',
                                  backgroundColor: 'primary.50',
                                },
                              }}
                            >
                              + Custom Time
                            </Button>
                          ) : (
                            <Box sx={{ width: '100%' }}>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', width: '100%' }}>
                                <TextField
                                  size="small"
                                  placeholder="HH:MM"
                                  value={customTimeInput}
                                  onChange={(e) => {
                                    setCustomTimeInput(e.target.value);
                                    if (customTimeError) {
                                      setCustomTimeError('');
                                    }
                                  }}
                                  inputProps={{
                                    pattern: '[0-9]{2}:[0-9]{2}',
                                    maxLength: 5,
                                  }}
                                  error={!!customTimeError}
                                  sx={{ flexGrow: 1 }}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      handleCustomTimeSubmit();
                                    }
                                  }}
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={handleCustomTimeSubmit}
                                  disabled={!customTimeInput}
                                >
                                  Add
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setShowCustomTimeInput(false);
                                    setCustomTimeInput('');
                                    setCustomTimeError('');
                                  }}
                                >
                                  Cancel
                                </Button>
                              </Box>
                              {customTimeError && (
                                <Typography
                                  variant="caption"
                                  color="error"
                                  sx={{
                                    display: 'block',
                                    mt: 0.5,
                                    fontSize: '0.75rem',
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {customTimeError}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 100,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || submitLoading}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 100,
          }}
        >
          {submitLoading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              {rescheduleMode ? 'Rescheduling...' : 'Adding...'}
            </>
          ) : rescheduleMode ? (
            'Reschedule Appointment'
          ) : (
            'Add Appointment'
          )}
        </Button>
      </DialogActions>

      {/* Sub-modals */}
      <AddServiceModal
        open={addServiceModalOpen}
        onClose={() => setAddServiceModalOpen(false)}
        onServiceAdded={(newService) => {
          setServices((prev) => [...prev, newService]);
          setSelectedServices((prev) => [...prev, newService]);
        }}
      />

      <AddCustomerModal
        open={addCustomerModalOpen}
        onClose={() => setAddCustomerModalOpen(false)}
        onCustomerAdded={handleAddCustomerAdded}
        garageId={garageId}
      />
    </Dialog>
  );
};

export default NewAppointmentModal;